import uniqid from 'uniqid';
import { isObject } from 'lodash';
import ReactiveComponent, { watch } from '@utils/reactive-component';
import AjaxLoader from '../ajax-loader/ajax-loader';
import isFn from '@utils/is-function';
import template from './templates/checkout-address-form';
import SelectBox from '../select-box/select-box';
import states from '@utils/states';

const EVENT_KEY = '_checkoutAddressForm_';
const EVENT_INPUT = `input.${EVENT_KEY}`;

const CLASS_VALIDATED = 'was-validated';
const CLASS_VISIBLE_ERROR = '-visible';

const DEFAULT_CONTEXT = {
    cancel: 'Cancel',
    heading: 'Add Address',
    requiredToken: '*',
    submit: 'Apply Updates',
    validation: '{0} is required'
};

export const DEFAULT_OPTIONS = {
    classList: '',
    onCancelHandler: null,
    replaceOriginEl: false,
    schema: {
        firstName: {
            required: true,
            name: 'FirstName',
            label: 'First Name',
            value: ''
        },
        lastName: {
            required: true,
            name: 'LastName',
            label: 'Last Name',
            value: ''
        },
        address: {
            required: true,
            name: 'Address1',
            label: 'Street Address',
            value: ''
        },
        address2: {
            required: false,
            name: 'Address2',
            label: 'Address Line 2',
            value: ''
        },
        city: {
            required: true,
            name: 'City',
            label: 'City',
            value: ''
        },
        state: {
            required: true,
            name: 'State',
            label: 'State',
            value: ''
        },
        zip: {
            required: true,
            name: 'Zip',
            label: 'Zip',
            value: ''
        }
    }
};

export default class CheckoutAddressForm extends ReactiveComponent {
    constructor($originEl, options, context) {
        super();

        options = { ...DEFAULT_OPTIONS, ...options };
        context = { ...DEFAULT_CONTEXT, ...context };

        const $parsed = $( $.parseHTML(template) );
        this.$el = $parsed;
        this.$originEl = $originEl;

        // non-field labels
        this.$cancelLabel = this.$el.find('[data-label="cancel"]');
        this.$headingLabel = this.$el.find('[data-label="heading"]');
        this.$submitLabel = this.$el.find('[data-label="submit"]');

        // field labels
        this.$firstNameLabel = this.$el.find('[data-label="firstName"]');
        this.$lastNameLabel = this.$el.find('[data-label="lastName"]');
        this.$addressLabel = this.$el.find('[data-label="address"]');
        this.$address2Label = this.$el.find('[data-label="address2"]');
        this.$cityLabel = this.$el.find('[data-label="city"]');
        this.$stateLabel = this.$el.find('[data-label="state"]');
        this.$zipLabel = this.$el.find('[data-label="zip"]');

        // field error labels
        this.$firstNameErrorLabel = this.$el.find('[data-label="firstNameError"]');
        this.$lastNameErrorLabel = this.$el.find('[data-label="lastNameError"]');
        this.$addressErrorLabel = this.$el.find('[data-label="addressError"]');
        this.$address2ErrorLabel = this.$el.find('[data-label="address2Error"]');
        this.$cityErrorLabel = this.$el.find('[data-label="cityError"]');
        this.$stateErrorLabel = this.$el.find('[data-label="stateError"]');
        this.$zipErrorLabel = this.$el.find('[data-label="zipError"]');
        
        // field errors
        this.$error = this.$el.find('[data-region="error"]');
        this.$firstNameError = this.$el.find('[data-region="firstNameError"]');
        this.$lastNameError = this.$el.find('[data-region="lastNameError"]');
        this.$addressError = this.$el.find('[data-region="addressError"]');
        this.$address2Error = this.$el.find('[data-region="address2Error"]');
        this.$cityError = this.$el.find('[data-region="cityError"]');
        this.$stateError = this.$el.find('[data-region="stateError"]');
        this.$zipError = this.$el.find('[data-region="zipError"]');

        // field controls (input)
        this.$firstNameCtrl = this.$el.find('[data-control="firstName"]');
        this.$lastNameCtrl = this.$el.find('[data-control="lastName"]');
        this.$addressCtrl = this.$el.find('[data-control="address"]');
        this.$address2Ctrl = this.$el.find('[data-control="address2"]');
        this.$cityCtrl = this.$el.find('[data-control="city"]');
        this.$stateCtrl = this.$el.find('[data-control="state"]');
        this.$zipCtrl = this.$el.find('[data-control="zip"]');

        this.$cancel = this.$el.find('[data-control="cancel"]');

        this.$loaderPlaceholder = this.$el.find('[data-placeholder="loader"]');
        this.$stateSelectorPlaceholder = this.$el.find('[data-placeholder="state"]');

        this.loader = null;
        this.stateSelector = null;

        this.replaceValidationToken = (str) => context.validation.replace('{0}', str);

        this.classList = options.classList;
        this.onCancelHandler = isFn(options.onCancelHandler) || null;
        this.replaceOriginEl = options.replaceOriginEl;
        this.rendered = false;
        this.schema = Object.fromEntries(
            Object.entries(options.schema).map(([ key, value ]) => {
                const id = uniqid.time(`${key}_`); // generate unique ids for each field
                const errorId = `${id}_error`;
                const error = value.error && value.required ? value.error : (context.validation?.replace('{0}', value.label) || 'This field is required');
                const label = !value.required ? value.label : value.label + context.requiredToken;

                return [ key, { ...value, error, errorId, id, label } ]
            })
        );

        this.reactive({
            loading: false,
            value: null
        });

        this.context = context;
    }

    [watch]() {
        return {
            loading(loading) {
                this.loader.active = loading;
            }
        }
    }

    init() {
        this.renderLabels();
        this.setupAccessibility();
        this.setupCancel();
        this.setupClassList();
        this.setupLoader();
        this.setupStateSelector();
        this.setupListeners();

        this.bindElementsToSchema();
    }

    bindElementsToSchema() {
        this.schema = Object.fromEntries(
            Object.entries(this.schema).map(([ key, value ]) => {
                const $el = this.$el.find(`#${value.id}`);
                const $errorEl = this.$el.find(`#${value.errorId}`);
                return [ key, { ...value, $el, $errorEl } ];
            })
        );
    }

    focus() {
        this.$el.find('input, select').get(0)?.focus();
    }

    hideError() {
        this.$error.hide();
    }

    onInvalidInput($el, $errorEl, errorId) {
        if($el.val()) {
            $el.attr({ 'aria-describedby': null, 'aria-invalid': false });
            $errorEl.removeClass(CLASS_VISIBLE_ERROR);
        } else {
            $el.attr({ 'aria-describedby': errorId, 'aria-invalid': true });
            $errorEl.addClass(CLASS_VISIBLE_ERROR);
        }
    }

    onSubmit(evt) {
        evt?.preventDefault();
        evt?.stopPropagation();

        const form = this.$el.get(0);

        if(!form.checkValidity()) {
            this.validate();

            if(this.value) {
                this.unsetValue();
            }
        } else {
            this.hideError();
            this.updateValue();
            this.resetValidation()
        }
    }

    render() {
        if(this.$originEl) {
            if(this.replaceOriginEl) {
                this.$originEl.replaceWith(this.$el);
            } else {
                this.$originEl.html(this.$el);
            }
        }

        this.rendered = true;

        return this.$el;
    }

    renderLabels() {
        this.$cancelLabel.html(this.context.cancel);
        this.$headingLabel.html(this.context.heading);
        this.$submitLabel.html(this.context.submit);

        this.$firstNameLabel.html(this.schema.firstName.label);
        this.$lastNameLabel.html(this.schema.lastName.label);
        this.$addressLabel.html(this.schema.address.label);
        this.$address2Label.html(this.schema.address2.label);
        this.$cityLabel.html(this.schema.city.label);
        this.$stateLabel.html(this.schema.state.label);
        this.$zipLabel.html(this.schema.zip.label);

        this.$firstNameCtrl.attr('placeholder', this.schema.firstName.label);
        this.$lastNameCtrl.attr('placeholder', this.schema.lastName.label);
        this.$addressCtrl.attr('placeholder', this.schema.address.label);
        this.$address2Ctrl.attr('placeholder', this.schema.address2.label);
        this.$cityCtrl.attr('placeholder', this.schema.city.label);
        this.$stateCtrl.attr('placeholder', this.schema.state.label);
        this.$zipCtrl.attr('placeholder', this.schema.zip.label);

        this.$firstNameErrorLabel.html(this.schema.firstName.error);
        this.$lastNameErrorLabel.html(this.schema.lastName.error);
        this.$addressErrorLabel.html(this.schema.address.error);
        this.$address2ErrorLabel.html(this.schema.address2.error);
        this.$cityErrorLabel.html(this.schema.city.error);
        this.$stateErrorLabel.html(this.schema.state.error);
        this.$zipErrorLabel.html(this.schema.zip.error);
    }

    setHeading(heading) {
        this.$headingLabel.html(heading);
    }

    setValues(values) {
        if(isObject(values) && !Array.isArray(values)) {
            Object.entries(values).forEach(([ key, value ]) => {
                if(this.schema[key]) {
                    if(key !== 'state') {
                        this.schema[key].value = value;
                        this.schema[key].$el.val(value);    
                    } else {
                        this.stateSelector.setValue(value);
                    }
                }
            });
        }
    }

    setupClassList() {
        this.$el.addClass(this.classList);
    }

    resetValidation() {
        this.$el.removeClass(CLASS_VALIDATED);

        Object.values(this.schema).forEach(({ $el, $errorEl }) => {
            $errorEl.removeClass(CLASS_VISIBLE_ERROR);
            $el.attr({ 'aria-describedby': null, 'aria-invalid': null });
            $el.off(EVENT_INPUT);
        });
    }

    resetValues() {
        Object.entries(this.schema).forEach(([ key, { $el } ]) => {
            if(key !== 'state') {
                $el.val('');
            } else {
                this.stateSelector.setValue('');
            }
        });
    }

    setupAccessibility() {
        this.$firstNameLabel.attr('for', this.schema.firstName.id);
        this.$lastNameLabel.attr('for', this.schema.lastName.id);
        this.$addressLabel.attr('for', this.schema.address.id);
        this.$address2Label.attr('for', this.schema.address2.id);
        this.$cityLabel.attr('for', this.schema.city.id);
        this.$stateLabel.attr('for', this.schema.state.id);
        this.$zipLabel.attr('for', this.schema.zip.id);

        this.$firstNameCtrl.attr({ 'id': this.schema.firstName.id, 'name': this.schema.firstName.name });
        this.$lastNameCtrl.attr({ 'id': this.schema.lastName.id, 'name': this.schema.lastName.name });
        this.$addressCtrl.attr({ 'id': this.schema.address.id, 'name': this.schema.address.name });
        this.$address2Ctrl.attr({ 'id': this.schema.address2.id, 'name': this.schema.address2.name });
        this.$cityCtrl.attr({ 'id': this.schema.city.id, 'name': this.schema.city.name });
        this.$stateCtrl.attr({ 'id': this.schema.state.id, 'name': this.schema.state.name });
        this.$zipCtrl.attr({ 'id': this.schema.zip.id, 'name': this.schema.zip.name });

        this.$firstNameError.attr('id', this.schema.firstName.errorId);
        this.$lastNameError.attr('id', this.schema.lastName.errorId);
        this.$addressError.attr('id', this.schema.address.errorId);
        this.$address2Error.attr('id', this.schema.address2.errorId);
        this.$cityError.attr('id', this.schema.city.errorId);
        this.$stateError.attr('id', this.schema.state.errorId);
        this.$zipError.attr('id', this.schema.zip.errorId);
    }

    setupCancel() {
        if(this.onCancelHandler) {
            this.$cancel.on('click', () => this.onCancelHandler());
        } else {
            this.$cancel.hide();
        }
    }

    setupLoader() {
        this.loader = new AjaxLoader(this.$loaderPlaceholder, {
            replaceOriginEl: false,
            size: 'xs'
        });

        this.loader.render();
    }

    setupListeners() {
        this.$el.on('submit', (evt) => this.onSubmit(evt));
    }
    
    setupStateSelector() {
        this.stateSelector = new SelectBox(this.$stateSelectorPlaceholder, {
            id: this.schema.state.id,
            mods: '-has-placeholder -txt-base -arrow-sm',
            name: this.schema.state.name,
            options: states,
            required: this.schema.state.required,
            unselectedText: this.schema.state.label
        });
        this.stateSelector.render();
    }

    showError(error) {
        this.$error.show().html(error);
    }

    validate() {
        this.$el.addClass(CLASS_VALIDATED);

        Object.values(this.schema).forEach(field => {
            if(field.required && !field.$el.val()) {
                field.$el.attr({ 'aria-describedby': field.errorId, 'aria-invalid': true });
                field.$el.on(EVENT_INPUT, () => this.onInvalidInput(field.$el, field.$errorEl, field.errorId));

                field.$errorEl.addClass(CLASS_VISIBLE_ERROR);
            }
        });
    }

    watchValue(fn) {
        if(isFn(fn)) {
            return this.watch('value', fn);
        }
    }

    updateValue() {
        let value = {};

        Object.values(this.schema).forEach(({ $el, name }) => {
            const val = $el.val();
            value[name] = val;
        });

        this.value = value;
    }

    unsetValue() {
        this.value = null;
    }
}
